import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Calendar, LogOut, FileText, Clock, User, 
  Plus, Upload, MessageSquare, CheckCircle2, AlertTriangle, 
  ChevronRight, ChevronLeft, Home, Camera, FileVideo, File,
  Shield, Wrench, X, RefreshCw, Eye
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== API FUNCTIONS ====================
const api = {
  async login(email, password) {
    const res = await axios.post(`${API}/api/portal/login`, { email, password });
    return res.data;
  },
  async getDashboard(token) {
    const res = await axios.get(`${API}/api/client-portal/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async getJobs(token) {
    const res = await axios.get(`${API}/api/client-portal/jobs`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async getJob(token, jobId) {
    const res = await axios.get(`${API}/api/client-portal/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async createJob(payload) {
    const res = await axios.post(`${API}/api/client-portal/jobs`, payload);
    return res.data;
  },
  async addNote(token, jobId, message, note_type) {
    const res = await axios.post(`${API}/api/client-portal/jobs/${jobId}/notes`, 
      { message, note_type },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    return res.data;
  },
  async uploadFile(token, jobId, file, file_category, caption) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_category', file_category);
    if (caption) formData.append('caption', caption);
    const res = await axios.post(`${API}/api/client-portal/jobs/${jobId}/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  }
};

// ==================== INTAKE WIZARD ====================
const INSURANCE_ISSUE_TYPES = [
  'Storm Damage',
  'Water Leak / Escape of Water',
  'Structural Movement / Subsidence',
  'Fire Damage',
  'Impact Damage',
  'Accidental Damage',
  'Flood Damage',
  'Other Claim'
];

const MAINTENANCE_ISSUE_TYPES = [
  'Plumbing / Leak',
  'Roofing / Water Ingress',
  'Base / Support / Level Issue',
  'Doors / Windows / External',
  'Internal Damage / Finishes',
  'Drainage Issue',
  'Ground Movement / Surface Issue',
  'Electrical / Heating',
  'General Repair',
  'Other Maintenance Issue'
];

const FILE_CATEGORIES = [
  'Damage Photos',
  'Close-Up Photos',
  'Wide Area Photos',
  'Video Evidence',
  'Estimate / Invoice',
  'Policy / Claim Document',
  'Site Notice / Park Correspondence',
  'Certificate',
  'Other Document'
];

const CONTACT_ROLES = [
  'Policyholder',
  'Park Owner',
  'Park Manager',
  'Resident',
  'Contractor',
  'Adjuster / Claims Handler',
  'Other'
];

function IntakeWizard({ onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    intake_mode: '',
    issue_type: '',
    site_name: '',
    plot_number: '',
    address_line_1: '',
    address_line_2: '',
    town_city: '',
    county: '',
    postcode: '',
    region: '',
    incident_date: '',
    incident_time: '',
    occupied_flag: null,
    safe_flag: null,
    temporary_action_flag: null,
    temporary_action_description: '',
    issue_description: '',
    // Insurance fields
    insurer_name: '',
    policy_number: '',
    claim_reference: '',
    insurer_notified_date: '',
    adjuster_name: '',
    adjuster_email: '',
    claim_stage: 'new',
    suspected_cause: '',
    prior_issues_flag: null,
    prior_repairs_flag: null,
    sudden_damage_flag: null,
    // Maintenance fields
    affected_scope: 'single',
    active_damage_flag: null,
    urgent_attendance_flag: null,
    access_available_flag: null,
    reported_previously_flag: null,
    existing_contractor_flag: null,
    existing_contractor_name: '',
    previous_report_reference: '',
    // Contact
    client_name: '',
    company_name: '',
    client_email: '',
    client_phone: '',
    contact_role: '',
    preferred_contact_method: 'email',
    preferred_contact_time: '',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = formData.intake_mode === 'insurance_claim' ? 9 : 9;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files).map(f => ({
      file: f,
      category: 'Damage Photos',
      caption: '',
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const updateFileCategory = (idx, category) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, category } : f));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await api.createJob(formData);
      toast.success(`Job submitted! Reference: ${result.job_reference}`);
      onComplete(result);
    } catch (err) {
      // Handle validation errors (array of error objects) or string error messages
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to submit job';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Extract first validation error message
        errorMessage = detail[0]?.msg || 'Validation error';
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.intake_mode;
      case 2: return !!formData.issue_type;
      case 3: return formData.site_name && formData.address_line_1 && formData.postcode;
      case 4: return true;
      case 5: return formData.issue_description?.length > 10;
      case 6: return true;
      case 7: return true;
      case 8: return formData.client_name && formData.client_email && formData.client_phone;
      case 9: return true;
      default: return true;
    }
  };

  // Step 1: Mode Selection
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">What do you need help with?</h2>
        <p className="text-slate-500 mt-2">Select the type of issue you're reporting</p>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={() => updateField('intake_mode', 'insurance_claim')}
          className={`p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg ${
            formData.intake_mode === 'insurance_claim' 
              ? 'border-[#1F7A78] bg-[#1F7A78]/5 shadow-md' 
              : 'border-slate-200 hover:border-[#1F7A78]/50'
          }`}
          data-testid="mode-insurance"
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${formData.intake_mode === 'insurance_claim' ? 'bg-[#1F7A78] text-white' : 'bg-slate-100 text-slate-600'}`}>
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-slate-900">Insurance Claim</h3>
              <p className="text-sm text-slate-500 mt-1">Report insured damage, submit a claim, or follow up on an existing insurance matter</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => updateField('intake_mode', 'maintenance_issue')}
          className={`p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg ${
            formData.intake_mode === 'maintenance_issue' 
              ? 'border-[#1F7A78] bg-[#1F7A78]/5 shadow-md' 
              : 'border-slate-200 hover:border-[#1F7A78]/50'
          }`}
          data-testid="mode-maintenance"
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${formData.intake_mode === 'maintenance_issue' ? 'bg-[#1F7A78] text-white' : 'bg-slate-100 text-slate-600'}`}>
              <Wrench className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-slate-900">Park / Maintenance Issue</h3>
              <p className="text-sm text-slate-500 mt-1">Report repairs, maintenance issues, or operational problems</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  // Step 2: Issue Type
  const renderStep2 = () => {
    const issueTypes = formData.intake_mode === 'insurance_claim' ? INSURANCE_ISSUE_TYPES : MAINTENANCE_ISSUE_TYPES;
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-900">What type of issue is this?</h2>
          <p className="text-slate-500 mt-2">Select the category that best describes your {formData.intake_mode === 'insurance_claim' ? 'claim' : 'issue'}</p>
        </div>
        
        <div className="grid gap-3 sm:grid-cols-2">
          {issueTypes.map(type => (
            <button
              key={type}
              onClick={() => updateField('issue_type', type)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                formData.issue_type === type 
                  ? 'border-[#1F7A78] bg-[#1F7A78]/5' 
                  : 'border-slate-200 hover:border-[#1F7A78]/50'
              }`}
              data-testid={`issue-type-${type.toLowerCase().replace(/\s/g, '-')}`}
            >
              <span className="font-medium text-slate-900">{type}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Step 3: Location
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">Location Details</h2>
        <p className="text-slate-500 mt-2">Where is the property located?</p>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Park / Site Name *</Label>
          <Input
            value={formData.site_name}
            onChange={(e) => updateField('site_name', e.target.value)}
            placeholder="e.g. Wyldecrest Parks"
            className="rounded-xl"
            data-testid="site-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Plot / Unit Number</Label>
          <Input
            value={formData.plot_number}
            onChange={(e) => updateField('plot_number', e.target.value)}
            placeholder="e.g. 42A"
            className="rounded-xl"
            data-testid="plot-number"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Address Line 1 *</Label>
        <Input
          value={formData.address_line_1}
          onChange={(e) => updateField('address_line_1', e.target.value)}
          placeholder="Street address"
          className="rounded-xl"
          data-testid="address-line-1"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Address Line 2</Label>
        <Input
          value={formData.address_line_2}
          onChange={(e) => updateField('address_line_2', e.target.value)}
          placeholder="Apartment, suite, etc."
          className="rounded-xl"
        />
      </div>
      
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label>Town / City</Label>
          <Input
            value={formData.town_city}
            onChange={(e) => updateField('town_city', e.target.value)}
            className="rounded-xl"
          />
        </div>
        <div className="space-y-2">
          <Label>County</Label>
          <Input
            value={formData.county}
            onChange={(e) => updateField('county', e.target.value)}
            className="rounded-xl"
          />
        </div>
        <div className="space-y-2">
          <Label>Postcode *</Label>
          <Input
            value={formData.postcode}
            onChange={(e) => updateField('postcode', e.target.value.toUpperCase())}
            placeholder="e.g. CM12 3AB"
            className="rounded-xl"
            data-testid="postcode"
          />
        </div>
      </div>
    </div>
  );

  // Step 4: Incident Timing
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">When did this happen?</h2>
        <p className="text-slate-500 mt-2">Help us understand the timeline</p>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Date incident happened / first noticed</Label>
          <Input
            type="date"
            value={formData.incident_date}
            onChange={(e) => updateField('incident_date', e.target.value)}
            className="rounded-xl"
            data-testid="incident-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Time (optional)</Label>
          <Input
            type="time"
            value={formData.incident_time}
            onChange={(e) => updateField('incident_time', e.target.value)}
            className="rounded-xl"
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Is the unit currently occupied?</Label>
          <div className="flex gap-3">
            {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
              <button
                key={String(opt.v)}
                onClick={() => updateField('occupied_flag', opt.v)}
                className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                  formData.occupied_flag === opt.v 
                    ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                    : 'border-slate-200 hover:border-[#1F7A78]/50'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Is the unit safe to remain in?</Label>
          <div className="flex gap-3">
            {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }, { v: null, l: 'Unsure' }].map(opt => (
              <button
                key={String(opt.v)}
                onClick={() => updateField('safe_flag', opt.v)}
                className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                  formData.safe_flag === opt.v 
                    ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                    : 'border-slate-200 hover:border-[#1F7A78]/50'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Has any temporary repair or mitigation been carried out?</Label>
          <div className="flex gap-3">
            {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
              <button
                key={String(opt.v)}
                onClick={() => updateField('temporary_action_flag', opt.v)}
                className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                  formData.temporary_action_flag === opt.v 
                    ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                    : 'border-slate-200 hover:border-[#1F7A78]/50'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {formData.temporary_action_flag && (
          <div className="space-y-2">
            <Label>Describe the temporary action taken</Label>
            <Textarea
              value={formData.temporary_action_description}
              onChange={(e) => updateField('temporary_action_description', e.target.value)}
              placeholder="What temporary repairs or mitigation have been done?"
              className="rounded-xl"
              rows={3}
            />
          </div>
        )}
      </div>
    </div>
  );

  // Step 5: Description
  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">Describe the Issue</h2>
        <p className="text-slate-500 mt-2">
          {formData.intake_mode === 'insurance_claim' 
            ? 'Please describe what happened, when it happened, what damage is visible, and whether any mitigation or emergency works have been carried out.'
            : 'Please describe the issue, when it was first noticed, how it is affecting the unit or site, and whether any temporary action has been taken.'}
        </p>
      </div>
      
      <div className="space-y-2">
        <Label>Issue Description *</Label>
        <Textarea
          value={formData.issue_description}
          onChange={(e) => updateField('issue_description', e.target.value)}
          placeholder="Provide as much detail as possible..."
          className="rounded-xl min-h-[200px]"
          data-testid="issue-description"
        />
        <p className="text-sm text-slate-500">{formData.issue_description?.length || 0} characters</p>
      </div>
    </div>
  );

  // Step 6: Evidence Uploads
  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">Upload Evidence</h2>
        <p className="text-slate-500 mt-2">Add photos, videos, or documents to support your {formData.intake_mode === 'insurance_claim' ? 'claim' : 'report'}</p>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-[#1F7A78] hover:bg-[#1F7A78]/5 transition-all">
          <Camera className="h-8 w-8 text-slate-400 mb-2" />
          <span className="text-sm font-medium text-slate-700">Add Photos</span>
          <input type="file" accept="image/*" multiple onChange={handleFileAdd} className="hidden" />
        </label>
        
        <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-[#1F7A78] hover:bg-[#1F7A78]/5 transition-all">
          <FileVideo className="h-8 w-8 text-slate-400 mb-2" />
          <span className="text-sm font-medium text-slate-700">Add Videos</span>
          <input type="file" accept="video/*" multiple onChange={handleFileAdd} className="hidden" />
        </label>
        
        <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-[#1F7A78] hover:bg-[#1F7A78]/5 transition-all">
          <File className="h-8 w-8 text-slate-400 mb-2" />
          <span className="text-sm font-medium text-slate-700">Add Documents</span>
          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple onChange={handleFileAdd} className="hidden" />
        </label>
      </div>

      {files.length > 0 && (
        <div className="space-y-3 mt-6">
          <h3 className="font-medium text-slate-900">Uploaded Files ({files.length})</h3>
          {files.map((f, idx) => (
            <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
              {f.preview && <img src={f.preview} alt="" className="w-12 h-12 object-cover rounded-lg" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{f.file.name}</p>
                <select 
                  value={f.category} 
                  onChange={(e) => updateFileCategory(idx, e.target.value)}
                  className="text-xs text-slate-500 bg-transparent border-none p-0 focus:ring-0"
                >
                  {FILE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700">
                <X className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-slate-500 text-center">You can upload more files later from your dashboard</p>
    </div>
  );

  // Step 7: Mode-specific questions
  const renderStep7 = () => {
    if (formData.intake_mode === 'insurance_claim') {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Claim Details</h2>
            <p className="text-slate-500 mt-2">Provide insurance and claim information</p>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Insurer Name</Label>
              <Input
                value={formData.insurer_name}
                onChange={(e) => updateField('insurer_name', e.target.value)}
                placeholder="e.g. Aviva, NFU Mutual"
                className="rounded-xl"
                data-testid="insurer-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Policy Number</Label>
              <Input
                value={formData.policy_number}
                onChange={(e) => updateField('policy_number', e.target.value)}
                placeholder="Your policy number"
                className="rounded-xl"
                data-testid="policy-number"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Claim Reference (if known)</Label>
              <Input
                value={formData.claim_reference}
                onChange={(e) => updateField('claim_reference', e.target.value)}
                placeholder="Existing claim reference"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Date Insurer Notified</Label>
              <Input
                type="date"
                value={formData.insurer_notified_date}
                onChange={(e) => updateField('insurer_notified_date', e.target.value)}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Has an adjuster / claims handler been appointed?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('existing_contractor_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.existing_contractor_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {formData.existing_contractor_flag && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Adjuster / Handler Name</Label>
                  <Input
                    value={formData.adjuster_name}
                    onChange={(e) => updateField('adjuster_name', e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adjuster / Handler Email</Label>
                  <Input
                    type="email"
                    value={formData.adjuster_email}
                    onChange={(e) => updateField('adjuster_email', e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Was the damage sudden and identifiable?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }, { v: null, l: 'Unsure' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('sudden_damage_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.sudden_damage_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Has this item/area had similar issues before?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('prior_issues_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.prior_issues_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Maintenance mode
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Issue Details</h2>
            <p className="text-slate-500 mt-2">Help us understand the urgency and scope</p>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Is this affecting one unit or multiple units?</Label>
              <div className="flex gap-3">
                {[{ v: 'single', l: 'Single Unit' }, { v: 'multiple', l: 'Multiple Units' }].map(opt => (
                  <button
                    key={opt.v}
                    onClick={() => updateField('affected_scope', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.affected_scope === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Is this causing active damage now?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('active_damage_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.active_damage_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Is urgent attendance required?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes, urgent' }, { v: false, l: 'No, not urgent' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('urgent_attendance_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.urgent_attendance_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Is access to the property available?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No - needs arrangement' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('access_available_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.access_available_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Has this been reported previously?</Label>
              <div className="flex gap-3">
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => updateField('reported_previously_flag', opt.v)}
                    className={`px-6 py-2 rounded-xl border-2 font-medium transition-all ${
                      formData.reported_previously_flag === opt.v 
                        ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                        : 'border-slate-200 hover:border-[#1F7A78]/50'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {formData.reported_previously_flag && (
              <div className="space-y-2">
                <Label>Previous Report Reference</Label>
                <Input
                  value={formData.previous_report_reference}
                  onChange={(e) => updateField('previous_report_reference', e.target.value)}
                  placeholder="Previous job/report reference if known"
                  className="rounded-xl"
                />
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  // Step 8: Contact Details
  const renderStep8 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">Your Contact Details</h2>
        <p className="text-slate-500 mt-2">How can we reach you?</p>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Your Name *</Label>
          <Input
            value={formData.client_name}
            onChange={(e) => updateField('client_name', e.target.value)}
            placeholder="Full name"
            className="rounded-xl"
            data-testid="client-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Company Name (optional)</Label>
          <Input
            value={formData.company_name}
            onChange={(e) => updateField('company_name', e.target.value)}
            placeholder="Company or park name"
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Email Address *</Label>
          <Input
            type="email"
            value={formData.client_email}
            onChange={(e) => updateField('client_email', e.target.value)}
            placeholder="your@email.com"
            className="rounded-xl"
            data-testid="client-email"
          />
        </div>
        <div className="space-y-2">
          <Label>Phone Number *</Label>
          <Input
            type="tel"
            value={formData.client_phone}
            onChange={(e) => updateField('client_phone', e.target.value)}
            placeholder="07xxx xxxxxx"
            className="rounded-xl"
            data-testid="client-phone"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Your Role</Label>
          <select 
            value={formData.contact_role} 
            onChange={(e) => updateField('contact_role', e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Select role...</option>
            {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Preferred Contact Method</Label>
          <div className="flex gap-3">
            {['email', 'phone'].map(m => (
              <button
                key={m}
                onClick={() => updateField('preferred_contact_method', m)}
                className={`px-6 py-2 rounded-xl border-2 font-medium transition-all capitalize ${
                  formData.preferred_contact_method === m 
                    ? 'border-[#1F7A78] bg-[#1F7A78] text-white' 
                    : 'border-slate-200 hover:border-[#1F7A78]/50'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Step 9: Review & Submit
  const renderStep9 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900">Review & Submit</h2>
        <p className="text-slate-500 mt-2">Please check your information before submitting</p>
      </div>
      
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-medium text-slate-900 mb-2">Type</h3>
          <div className="flex gap-2">
            <Badge className={formData.intake_mode === 'insurance_claim' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
              {formData.intake_mode === 'insurance_claim' ? 'Insurance Claim' : 'Maintenance Issue'}
            </Badge>
            <Badge variant="outline">{formData.issue_type}</Badge>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-medium text-slate-900 mb-2">Location</h3>
          <p className="text-slate-700">{formData.site_name}{formData.plot_number && `, Plot ${formData.plot_number}`}</p>
          <p className="text-slate-500 text-sm">{formData.address_line_1}, {formData.postcode}</p>
        </div>

        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-medium text-slate-900 mb-2">Description</h3>
          <p className="text-slate-700 text-sm whitespace-pre-wrap">{formData.issue_description}</p>
        </div>

        {formData.intake_mode === 'insurance_claim' && formData.insurer_name && (
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-medium text-slate-900 mb-2">Insurance Details</h3>
            <p className="text-slate-700">Insurer: {formData.insurer_name}</p>
            {formData.policy_number && <p className="text-slate-500 text-sm">Policy: {formData.policy_number}</p>}
          </div>
        )}

        <div className="bg-slate-50 rounded-xl p-4">
          <h3 className="font-medium text-slate-900 mb-2">Contact</h3>
          <p className="text-slate-700">{formData.client_name}</p>
          <p className="text-slate-500 text-sm">{formData.client_email} | {formData.client_phone}</p>
        </div>

        {files.length > 0 && (
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-medium text-slate-900 mb-2">Files ({files.length})</h3>
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <Badge key={i} variant="outline">{f.file.name}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      case 9: return renderStep9();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1F7A78] to-[#005456] text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/phs-logo.png" 
              alt="Park Home Support" 
              className="h-10"
            />
            <div>
              <h1 className="font-semibold">Report an Issue</h1>
              <p className="text-sm text-white/80">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={onCancel}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Progress bar */}
        <div className="max-w-4xl mx-auto px-4 pb-4">
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardContent className="p-6 sm:p-8">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          {step < totalSteps ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="gap-2 bg-[#1F7A78] hover:bg-[#005456]"
              data-testid="next-step"
            >
              Continue
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canProceed()}
              className="gap-2 bg-[#1F7A78] hover:bg-[#005456]"
              data-testid="submit-job"
            >
              {submitting ? 'Submitting...' : 'Submit'}
              <CheckCircle2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

// ==================== JOB DETAIL PANEL ====================
function JobDetailPanel({ job, notes, files, scheduledVisit, contractor, onAddNote, onUploadFile, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [noteMessage, setNoteMessage] = useState('');
  const [noteType, setNoteType] = useState('General Update');
  const [submitting, setSubmitting] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'bg-blue-100 text-blue-800',
      'Awaiting Review': 'bg-purple-100 text-purple-800',
      'Awaiting Triage': 'bg-amber-100 text-amber-800',
      'Booked Inspection': 'bg-cyan-100 text-cyan-800',
      'Visit Required': 'bg-orange-100 text-orange-800',
      'Scheduled': 'bg-indigo-100 text-indigo-800',
      'Inspected': 'bg-teal-100 text-teal-800',
      'In Progress': 'bg-sky-100 text-sky-800',
      'Completed': 'bg-green-100 text-green-800',
      'Closed': 'bg-slate-100 text-slate-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const handleAddNote = async () => {
    if (!noteMessage.trim()) return;
    setSubmitting(true);
    try {
      await onAddNote(noteMessage, noteType);
      setNoteMessage('');
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'uploads', label: 'Uploads', icon: Upload },
    { id: 'visits', label: 'Visits', icon: Calendar },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-bold text-[#1F7A78]">{job.job_reference}</h2>
              <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
            </div>
            <div className="flex gap-2 mb-2">
              <Badge className={job.intake_mode === 'insurance_claim' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                {job.intake_mode === 'insurance_claim' ? 'Insurance Claim' : 'Maintenance'}
              </Badge>
              <Badge variant="outline">{job.issue_type}</Badge>
            </div>
            <p className="text-sm text-slate-600 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {job.site_name}{job.plot_number && `, Plot ${job.plot_number}`}
            </p>
            <p className="text-xs text-slate-500">{job.full_address || job.address_line_1}, {job.postcode}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="sm:hidden">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-t border-slate-100 pt-3 -mx-4 px-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-[#1F7A78] text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="font-medium text-slate-900 mb-2">Description</h3>
            <p className="text-slate-700 text-sm whitespace-pre-wrap">{job.issue_description}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <h3 className="font-medium text-slate-900 mb-2">Incident Details</h3>
              <div className="space-y-1 text-sm">
                <p><span className="text-slate-500">Date:</span> <span className="text-slate-700">{job.incident_date || 'Not specified'}</span></p>
                <p><span className="text-slate-500">Occupied:</span> <span className="text-slate-700">{job.occupied_flag ? 'Yes' : job.occupied_flag === false ? 'No' : 'Unknown'}</span></p>
                <p><span className="text-slate-500">Safe:</span> <span className="text-slate-700">{job.safe_flag ? 'Yes' : job.safe_flag === false ? 'No' : 'Unknown'}</span></p>
              </div>
            </div>

            {job.intake_mode === 'insurance_claim' && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                <h3 className="font-medium text-slate-900 mb-2">Insurance Details</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-500">Insurer:</span> <span className="text-slate-700">{job.insurer_name || 'Not specified'}</span></p>
                  <p><span className="text-slate-500">Policy:</span> <span className="text-slate-700">{job.policy_number || 'Not specified'}</span></p>
                  <p><span className="text-slate-500">Claim Ref:</span> <span className="text-slate-700">{job.claim_reference || 'Pending'}</span></p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="font-medium text-slate-900 mb-2">Timeline</h3>
            <div className="space-y-1 text-sm">
              <p><span className="text-slate-500">Submitted:</span> <span className="text-slate-700">{formatDate(job.created_at)}</span></p>
              <p><span className="text-slate-500">Last Updated:</span> <span className="text-slate-700">{formatDate(job.updated_at)}</span></p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <h3 className="font-medium text-slate-900 mb-4">Messages</h3>
          
          {/* Note list */}
          <div className="space-y-3 max-h-64 overflow-auto mb-4">
            {notes.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No messages yet</p>
            ) : (
              notes.map(note => (
                <div key={note.id} className="rounded-xl bg-slate-50 p-3 border-l-4 border-[#1F7A78]">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-slate-900">
                      {note.created_by_name}
                      <span className="text-slate-500 font-normal ml-1">({note.created_by_role})</span>
                    </p>
                    <p className="text-xs text-slate-500">{formatDate(note.created_at)}</p>
                  </div>
                  <Badge variant="outline" className="text-xs mb-2">{note.note_type}</Badge>
                  <p className="text-sm text-slate-700">{note.message}</p>
                </div>
              ))
            )}
          </div>

          {/* Add note form */}
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <select 
              value={noteType} 
              onChange={(e) => setNoteType(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option>General Update</option>
              <option>Access Information</option>
              <option>Evidence Update</option>
              <option>Appointment Query</option>
              <option>Claim Query</option>
            </select>
            <Textarea
              value={noteMessage}
              onChange={(e) => setNoteMessage(e.target.value)}
              placeholder="Add a message..."
              className="rounded-xl"
              rows={3}
            />
            <Button 
              onClick={handleAddNote}
              disabled={!noteMessage.trim() || submitting}
              className="w-full bg-[#1F7A78] hover:bg-[#005456]"
            >
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'uploads' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-slate-900">Uploaded Files ({files.length})</h3>
            <label className="cursor-pointer">
              <Button size="sm" className="bg-[#1F7A78] hover:bg-[#005456]">
                <Upload className="h-4 w-4 mr-2" />
                Upload More
              </Button>
              <input type="file" multiple onChange={(e) => onUploadFile(e.target.files)} className="hidden" />
            </label>
          </div>
          
          {files.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No files uploaded yet</p>
          ) : (
            <div className="space-y-2">
              {files.map(file => (
                <div key={file.id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{file.file_name}</p>
                      <p className="text-xs text-slate-500">{file.file_category}</p>
                    </div>
                  </div>
                  <a 
                    href={`${API}${file.file_url}`} 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-[#1F7A78] text-sm font-medium hover:underline"
                  >
                    View
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'visits' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <h3 className="font-medium text-slate-900 mb-4">Scheduled Visits</h3>
          
          {scheduledVisit?.date ? (
            <div className="bg-[#1F7A78]/5 rounded-xl p-4 border border-[#1F7A78]/20">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="h-5 w-5 text-[#1F7A78]" />
                <p className="font-medium text-slate-900">
                  {formatDate(scheduledVisit.date)}
                  {scheduledVisit.time && ` at ${scheduledVisit.time}`}
                </p>
              </div>
              {scheduledVisit.surveyor && (
                <p className="text-sm text-slate-600 ml-8">Surveyor: {scheduledVisit.surveyor}</p>
              )}
              {contractor && (
                <p className="text-sm text-slate-600 ml-8">Contractor: {contractor.name}</p>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No visits scheduled yet</p>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function ClientPortalPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard | intake | job-detail
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('client_portal_token');
    const storedUser = localStorage.getItem('client_portal_user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsLoggedIn(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      loadDashboard();
    }
  }, [isLoggedIn, token]);

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const data = await api.getDashboard(token);
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await api.login(credentials.email, credentials.password);
      localStorage.setItem('client_portal_token', res.token);
      localStorage.setItem('client_portal_user', JSON.stringify(res.user));
      setToken(res.token);
      setUser(res.user);
      setIsLoggedIn(true);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_portal_token');
    localStorage.removeItem('client_portal_user');
    setIsLoggedIn(false);
    setUser(null);
    setToken(null);
    setDashboard(null);
  };

  const openJob = async (job) => {
    try {
      const data = await api.getJob(token, job.id);
      setSelectedJob(data);
      setActiveView('job-detail');
    } catch (err) {
      toast.error('Failed to load job details');
    }
  };

  const handleAddNote = async (message, noteType) => {
    if (!selectedJob) return;
    await api.addNote(token, selectedJob.job.id, message, noteType);
    const refreshed = await api.getJob(token, selectedJob.job.id);
    setSelectedJob(refreshed);
  };

  const handleUploadFile = async (files) => {
    if (!selectedJob || !files.length) return;
    for (const file of files) {
      await api.uploadFile(token, selectedJob.job.id, file, 'Damage Photos', '');
    }
    const refreshed = await api.getJob(token, selectedJob.job.id);
    setSelectedJob(refreshed);
    toast.success('Files uploaded');
  };

  const handleIntakeComplete = (result) => {
    loadDashboard();
    setActiveView('dashboard');
    toast.success(`Job ${result.job_reference} submitted successfully`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'bg-blue-100 text-blue-800 border-blue-200',
      'Awaiting Review': 'bg-purple-100 text-purple-800 border-purple-200',
      'Awaiting Triage': 'bg-amber-100 text-amber-800 border-amber-200',
      'Scheduled': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'In Progress': 'bg-sky-100 text-sky-800 border-sky-200',
      'Completed': 'bg-green-100 text-green-800 border-green-200',
      'Closed': 'bg-slate-100 text-slate-800 border-slate-200',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  // Show intake wizard
  if (activeView === 'intake') {
    return (
      <IntakeWizard 
        onComplete={handleIntakeComplete}
        onCancel={() => setActiveView('dashboard')}
      />
    );
  }

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-2">
            <img 
              src="/phs-logo.png" 
              alt="Park Home Support" 
              className="h-16 mx-auto mb-4"
            />
            <CardTitle className="text-[#1F7A78] text-2xl">Client Portal</CardTitle>
            <p className="text-stone-500 text-sm">Report issues or track your existing jobs</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="rounded-xl"
                  data-testid="client-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  className="rounded-xl"
                  data-testid="client-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#1F7A78] hover:bg-[#005456] rounded-xl h-11"
                disabled={loginLoading}
                data-testid="client-login-btn"
              >
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full rounded-xl h-11"
              onClick={() => setActiveView('intake')}
              data-testid="report-issue-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Report a New Issue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1F7A78] to-[#005456] text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/phs-logo.png" 
              alt="Park Home Support" 
              className="h-10"
            />
            <div className="hidden sm:block">
              <h1 className="font-semibold">Client Portal</h1>
              <p className="text-sm text-white/80">{user?.name || user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={() => {
                // Set the general token for the billing dashboard
                localStorage.setItem('token', token);
                window.location.href = '/client/dashboard';
              }}
              data-testid="billing-dashboard-btn"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Billing</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={loadDashboard}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F7A78] mx-auto"></div>
            <p className="mt-4 text-slate-500">Loading...</p>
          </div>
        ) : activeView === 'job-detail' && selectedJob ? (
          <>
            <Button 
              variant="ghost" 
              onClick={() => { setActiveView('dashboard'); setSelectedJob(null); }}
              className="mb-4"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <JobDetailPanel
              job={selectedJob.job}
              notes={selectedJob.notes || []}
              files={selectedJob.files || []}
              scheduledVisit={selectedJob.scheduled_visit}
              contractor={selectedJob.contractor}
              onAddNote={handleAddNote}
              onUploadFile={handleUploadFile}
              onClose={() => { setActiveView('dashboard'); setSelectedJob(null); }}
            />
          </>
        ) : (
          <>
            {/* Quick Actions */}
            <div className="mb-6">
              <Button 
                onClick={() => setActiveView('intake')}
                className="bg-[#1F7A78] hover:bg-[#005456] rounded-xl h-12 px-6"
                data-testid="new-issue-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Report a New Issue
              </Button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Open Jobs</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.kpis?.open_jobs || 0}</p>
                    </div>
                    <Briefcase className="h-8 w-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Awaiting Update</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.kpis?.awaiting_update || 0}</p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-indigo-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Scheduled Visits</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.kpis?.scheduled_visits || 0}</p>
                    </div>
                    <Calendar className="h-8 w-8 text-indigo-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Completed</p>
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.kpis?.completed_jobs || 0}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Jobs List */}
            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900">Your Jobs</h2>
              <p className="text-slate-500">Track the progress of your issues and claims</p>
            </div>

            {dashboard?.jobs?.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No jobs found</p>
                  <Button 
                    onClick={() => setActiveView('intake')}
                    className="mt-4 bg-[#1F7A78] hover:bg-[#005456]"
                  >
                    Report Your First Issue
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {dashboard?.jobs?.map(job => (
                  <Card 
                    key={job.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openJob(job)}
                    data-testid={`job-card-${job.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-[#1F7A78]">{job.job_reference}</span>
                            <Badge className={`${getStatusColor(job.status)} border`}>{job.status}</Badge>
                          </div>
                          <div className="flex gap-2 mb-2">
                            <Badge className={job.intake_mode === 'insurance_claim' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'} variant="outline">
                              {job.intake_mode === 'insurance_claim' ? 'Claim' : 'Maintenance'}
                            </Badge>
                            <Badge variant="outline">{job.issue_type}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {job.site_name}{job.plot_number && `, Plot ${job.plot_number}`}
                          </p>
                        </div>
                        <div className="text-right text-sm text-slate-500">
                          <p>{job.created_at && format(new Date(job.created_at), 'dd MMM yyyy')}</p>
                          <ChevronRight className="h-5 w-5 ml-auto mt-2 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          Leisure Home Solutions Ltd | admin@lhsportal.uk | 07557 017694
        </div>
      </footer>
    </div>
  );
}
